import { useLayoutEffect, useState, type RefObject } from "react";
import { EMAIL_BUILDER_CONTENT_WIDTH_PX } from "../emailBuilderVisualConstants";

const EMAIL_BUILDER_MOBILE_CANVAS_HORIZONTAL_INSET_PX = 48;

export interface IEmailBuilderMobileCanvasScale {
  scale: number;
  scaledHeightPx: number | null;
}

export function useEmailBuilderMobileCanvasScale(
  containerRef: RefObject<HTMLElement | null>,
  frameRef: RefObject<HTMLDivElement | null>,
  enabled: boolean,
): IEmailBuilderMobileCanvasScale {
  const [scale, setScale] = useState(1);
  const [scaledHeightPx, setScaledHeightPx] = useState<number | null>(null);

  useLayoutEffect(() => {
    if (!enabled) {
      setScale(1);
      setScaledHeightPx(null);
      return;
    }

    function updateScale() {
      const container = containerRef.current;
      const frame = frameRef.current;
      if (container === null || frame === null) {
        return;
      }

      const availableWidth = Math.max(
        0,
        container.clientWidth - EMAIL_BUILDER_MOBILE_CANVAS_HORIZONTAL_INSET_PX,
      );
      const nextScale = Math.min(1, availableWidth / EMAIL_BUILDER_CONTENT_WIDTH_PX);
      const normalizedScale = nextScale > 0 ? nextScale : 1;

      setScale(normalizedScale);
      setScaledHeightPx(Math.ceil(frame.offsetHeight * normalizedScale));
    }

    updateScale();

    const container = containerRef.current;
    const frame = frameRef.current;
    if (container === null || frame === null) {
      return;
    }

    const resizeObserver = new ResizeObserver(() => {
      updateScale();
    });

    resizeObserver.observe(container);
    resizeObserver.observe(frame);

    return () => {
      resizeObserver.disconnect();
    };
  }, [containerRef, enabled, frameRef]);

  return {
    scale,
    scaledHeightPx,
  };
}
